import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { AlumnoType } from '../../types/types';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, Modal, Radio, RadioGroup, Typography } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Edit } from '@mui/icons-material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AddAsistenciaParams, useAddAsistencia } from '../../hooks/addAsistencia';
import { ClosableTitle } from '../DialogTitle';
import { UserContext } from '../../contexts/userContext';
import { grey } from '@mui/material/colors';
import { IndicatorButton } from '../IndicatorButton';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

dayjs.extend(isBetweenPlugin);

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  ausente: boolean;
  presente: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== 'ausente' && prop !== 'presente',
})<CustomPickerDayProps>(({ theme, ausente, presente }) => ({
  ...(ausente && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    backgroundColor: theme.palette.error.light,
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.error.dark,
    },
    '&.MuiPickersDay-root': {
      '&.Mui-selected': {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.common.white,
        border: 3,
        borderColor: 'black',
        borderStyle: 'solid',
      },
    },
  }),
  ...(presente && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    backgroundColor: theme.palette.success.light,
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.success.dark,
    },
    '&.MuiPickersDay-root': {
      '&.Mui-selected': {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.common.white,
        border: 3,
        borderColor: 'black',
        borderStyle: 'solid',
      },
    },
  }),
  ...(!presente && !ausente && {
    '&.MuiPickersDay-root': {
      '&.Mui-selected': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: 3,
        borderColor: 'black',
        borderStyle: 'solid',
      },
    },
  }),
})) as React.ComponentType<CustomPickerDayProps>;

export interface CalendarioAsistenciaProps {
  alumno: AlumnoType | null;
  setAlumno: React.Dispatch<React.SetStateAction<AlumnoType | null>>
  refetchLista: () => void;
}
export default function CalendarioAsistencia(props:CalendarioAsistenciaProps) {
  const { alumno, setAlumno, refetchLista } = props;
  const [edition, setEdition] = useState(false);
  const [nuevoValor, setNuevoValor] = useState<'presente' | 'ausente' | 'sin-clases' | null>(null);
  const [nuevaObs, setNuevaObs] = useState<string | null>(null);
  const [nuevaAsistencia, setNuevaAsistencia] = useState<AddAsistenciaParams | null>();

  const { user } = useContext(UserContext);

  const inasistencias = useMemo(() => alumno?.asistencia?.filter((a) => !a?.asistencia) ?? [], [alumno] );
  const [currentInasistencia, setCurrentInasistencia] = useState<number>(inasistencias.length - 1);
  const [value, setValue] = React.useState<Dayjs | null>(dayjs(inasistencias?.[inasistencias.length - 1]?.dia ?? new Date()));

  useEffect(() => {
    setCurrentInasistencia(inasistencias.length - 1);
    setValue(dayjs(inasistencias?.[inasistencias.length - 1]?.dia ?? new Date()));
  }, [inasistencias]);

  const addAsistenciaApiCall = useAddAsistencia(nuevaAsistencia, user);

  useEffect(() => {
    if (addAsistenciaApiCall.data?._id && !addAsistenciaApiCall.isFetching) {
      setAlumno(addAsistenciaApiCall.data);
      setTimeout(() => refetchLista(), 500);
    }
    if (!addAsistenciaApiCall.isFetching) {
      setNuevoValor(null);
      setNuevaAsistencia(null);
      setEdition(false);
    }
  }, [addAsistenciaApiCall.data]);

  useEffect(() => {
    if (!addAsistenciaApiCall.isFetching) {
      setNuevoValor(null);
      setEdition(false);
    }
  }, [addAsistenciaApiCall.isFetching]);

  const renderWeekPickerDay = (
    date: Dayjs,
    selectedDates: Array<Dayjs | null>,
    pickersDayProps: PickersDayProps<Dayjs>,
  ) => {
    if (!alumno?.asistencia?.length) {
      return <PickersDay {...pickersDayProps} />;
    }
    const presente = alumno.asistencia?.some((diaAsistencia) => (
      date.isSame(diaAsistencia?.dia, 'day') && diaAsistencia?.asistencia
    ));
    const ausente = alumno.asistencia?.some((diaAsistencia) => (
      date.isSame(diaAsistencia?.dia, 'day') && !diaAsistencia?.asistencia
    ));

    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        ausente={ausente}
        presente={presente}
      />
    );
  };

  useEffect(() => {
    if (inasistencias[currentInasistencia]) setValue(dayjs(inasistencias[currentInasistencia].dia));
  }, [currentInasistencia, inasistencias]);

  if (!user?._id) return null;

  const diaAEditar = alumno?.asistencia?.find((i) => dayjs(i?.dia).isSame(value, 'day'));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={edition}
        onClose={() => setEdition(false)}
      >
        <DialogTitle id="asistencia-dialog">
          {`Editar asistencia del ${dayjs(value).format('DD/MM/YYYY')}`}
        </DialogTitle>
        <DialogContent>
          <RadioGroup
            value={nuevoValor}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNuevoValor((event.target as HTMLInputElement).value as 'presente' | 'ausente' | 'sin-clases');
            }}
          >
            <FormControlLabel value="presente" control={<Radio />} label="Presente" />
            <FormControlLabel value="ausente" control={<Radio />} label="Ausente" />
            <FormControlLabel value="sin-clases" control={<Radio />} label="No tuvo clases" />
          </RadioGroup>
          <TextField
            margin="dense"
            id="obs"
            label="Observaciones"
            type="text"
            fullWidth
            variant="standard"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNuevaObs(event.target.value);
            }}
            value={nuevaObs ?? ''}
          />
        </DialogContent>
        <DialogActions>
          <IndicatorButton
            onClick={() => setNuevaAsistencia({ id: alumno?._id, asistencia: nuevoValor, dia: value?.toISOString(), obs: nuevaObs ?? undefined })}
            label='Guardar Cambios'
            loading={addAsistenciaApiCall.isLoading}
          />
          <Button
            onClick={ () => {setNuevoValor(null); setNuevaObs(null); setEdition(false);}}
            variant="outlined"
            autoFocus
            disabled={addAsistenciaApiCall.isLoading}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
      <Modal
        open={!!alumno}
        onClose={() => setAlumno(null)}
        aria-labelledby={`Asistencia de ${alumno?.name} ${alumno?.apellido}`}
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <ClosableTitle id="closable-title-asistencia" onClose={() => setAlumno(null)}/>
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            label="Week picker"
            value={value}
            onChange={(newValue) => {
              setValue(newValue);
            }}
            renderDay={renderWeekPickerDay}
            renderInput={(params) => <TextField {...params} />}
            inputFormat="'Week of' MMM d"
          />
          <div style={{ width: '100%', height: 100, padding: 8, marginBottom: 10, backgroundColor: grey[100] }}>
            <Typography style={{ color: grey[600], fontSize: 14, textAlign: 'left' }}>{`Observaciones del ${dayjs(value).format('DD/MM')}`}</Typography>
            <Divider />
            {diaAEditar?.obs ?? ''}
          </div>
          <Box style={{ textAlign: 'center' }}>
            <Button onClick={() => {
              setEdition(true);
              if (diaAEditar) {
                setNuevaObs(diaAEditar.obs ?? '');
                if (diaAEditar.asistencia) setNuevoValor('presente');
                else setNuevoValor('ausente');
              } else {
                setNuevoValor('sin-clases');
                setNuevaObs('');
              }
            }}
            startIcon={<Edit/>}
            variant="contained"
            size='small'
            >
              {`Editar asistencia del ${dayjs(value).format('DD/MM')}`}
            </Button>
            <br/>
            {!!alumno?.asistencia?.length && (
              <>
                <IconButton
                  onClick={() => {
                    setCurrentInasistencia((prev) => prev - 1);
                  }}
                  disabled={currentInasistencia === 0}
                  color="info"
                  size='large'
                >
                  <ArrowBackIos/>
                </IconButton>
                  Buscar próxima inasistencia
                <IconButton
                  onClick={() => {
                    setCurrentInasistencia((prev) => prev + 1);
                  }}
                  disabled={currentInasistencia >= inasistencias.length - 1}
                  color="info"
                  size='large'
                >
                  <ArrowForwardIos/>
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </LocalizationProvider>
  );
}
